import { Component, HostListener, inject, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { AsyncPipe } from '@angular/common'
import { ActivatedRoute } from '@angular/router'
import { TranslateDirective } from '@ngx-translate/core'
import { filter, map, switchMap } from 'rxjs'
import { MeetingDetailsBarComponent } from '../meeting-details-bar/meeting-details-bar.component'
import { MeetingSetupComponent } from '../meeting-setup/meeting-setup.component'
import { Meeting, MeetingsService } from '../meetings.service'
import { TemplatesService } from '../templates.service'
import { UsersService } from '../users.service'
import { SpinnerComponent } from '../spinner/spinner.component'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { ComponentWithUnsavedChanges } from '../guards/unsaved-changes.guard'

@Component({
  selector: 'app-meeting-setup-page',
  standalone: true,
  imports: [AsyncPipe, TranslateDirective, MeetingDetailsBarComponent, MeetingSetupComponent, SpinnerComponent, ToolbarComponent],
  templateUrl: './meeting-setup-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './meeting-setup-page.component.scss',
})
export class MeetingSetupPageComponent implements ComponentWithUnsavedChanges {
  @ViewChild(MeetingSetupComponent)
  meetingSetupComponent: MeetingSetupComponent | undefined = undefined

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if (this.hasUnsavedChanges) {
      event.preventDefault()
    }
  }

  private route = inject(ActivatedRoute)
  private meetingsService = inject(MeetingsService)
  private templatesService = inject(TemplatesService)
  private usersService = inject(UsersService)

  meetingId$ = this.route.params.pipe(
    map((params) => (typeof params['id'] === 'string' ? parseInt(params['id'], 10) : null)),
    filter((id) => id !== null)
  )
  meeting$ = this.meetingId$.pipe(switchMap((id) => this.meetingsService.getMeeting(id!)))
  templateItems$ = this.meetingId$.pipe(
    switchMap((meetingId) => this.templatesService.getTemplateItems(meetingId)),
    map((result) => (result.isSuccess ? result.data : []))
  )

  currentUser$ = this.usersService.currentUser$

  get hasUnsavedChanges(): boolean {
    return this.meetingSetupComponent?.hasUnsavedChanges ?? false
  }

  getDeactivateDialogData() {
    if (this.meetingSetupComponent?.hasUnconfirmedTemplate && !this.meetingSetupComponent?.templateEditor?.hasUnsavedChanges) {
      return {
        titleKey: 'MEETING_SETUP.UNCONFIRMED_DIALOG.TITLE',
        descriptionKey: 'MEETING_SETUP.UNCONFIRMED_DIALOG.DESCRIPTION',
        confirmLabelKey: 'MEETING_SETUP.UNCONFIRMED_DIALOG.LEAVE',
        cancelLabelKey: 'MEETING_SETUP.UNCONFIRMED_DIALOG.STAY',
      }
    }
    return null
  }

  getBackButtonLink(meeting: Meeting): string[] {
    if (meeting.isUpcoming || meeting.isOngoing) {
      return ['/app', 'meetings']
    }
    return ['/app', 'meetings', '' + meeting.id]
  }
}
